import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Tooltip, alpha, useTheme } from '@mui/material'
import React from 'react'
import { useEscClose } from '../hooks/useEscClose'
import { FloatingActionBarProps } from './types'

/**
 * A floating action bar that appears at the bottom of the screen.
 * Used for mass update controls when rows are selected in a grid.
 *
 * Features:
 * - Fixed position at bottom of viewport
 * - Close button above the bar
 * - ESC key support to close
 * - "ESC to close" hint below
 *
 * @example
 * <FloatingActionBar
 *   visible={rowSelectionModel.ids.size > 0}
 *   onClose={handleClearSelection}
 * >
 *   <MassUpdateLeadsComponents {...props} />
 * </FloatingActionBar>
 */
export const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  visible,
  onClose,
  children,
  positions = {},
  styles = {},
  showEscHint = true,
  showCloseButton = true,
  sx,
}) => {
  const theme = useTheme()

  // Default positions
  const barBottom = positions.barBottom ?? 80
  const barHeight = positions.barHeight ?? 75
  const closeButtonSpacing = positions.closeButtonSpacing ?? 8
  const escHintBottom = positions.escHintBottom ?? 30

  // Default styles
  const width = styles.width ?? 'calc(100% - 120px)'
  const maxWidth = styles.maxWidth ?? '90vw'
  const zIndex = styles.zIndex ?? 1200
  const overlayZIndex = styles.overlayZIndex ?? 1300

  // Calculated positions
  const closeButtonBottom = barBottom + barHeight + closeButtonSpacing

  // ESC key handler
  useEscClose(onClose, visible)

  if (!visible) {
    return null
  }

  return (
    <>
      {/* Close Button Above Bar */}
      {showCloseButton && (
        <Box
          sx={{
            position: 'fixed',
            bottom: closeButtonBottom,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: overlayZIndex,
            backgroundColor: theme.palette.background.default,
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Tooltip title="Close" placement="top">
            <IconButton
              onClick={onClose}
              sx={{
                color: theme.palette.text.primary,
                width: '100%',
                height: '100%',
                border: `1px solid ${theme.palette.text.warning}`,
              }}
            >
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Main Floating Bar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: barBottom,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: zIndex,
          width: width,
          maxWidth: maxWidth,
          backgroundColor: theme.palette.background.input,
          boxShadow: `0 4px 20px ${alpha(theme.palette.text.primary, 0.4)}`,
          border: `1px solid ${theme.palette.text.secondary}`,
          borderRadius: 2,
          p: 1,
          minHeight: barHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          ...sx,
        }}
      >
        {children}
      </Box>

      {/* ESC Hint Below Bar */}
      {/* {showEscHint && (
        <Box
          sx={{
            position: 'fixed',
            bottom: escHintBottom,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: overlayZIndex,
            width: 'auto',
            maxWidth: '90vw',
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 5,
            backgroundColor: 'background.defaultdim',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.warning,
              borderRadius: 5,
              width: 'auto',
              p: 2,
              textAlign: 'center',
            }}
          >
            ESC to close
          </Typography>
        </Box>
      )} */}
    </>
  )
}

export default FloatingActionBar
